import {
	NUM_ACTIVE_BATTERS,
	NUM_ACTIVE_PITCHERS,
	NUM_STARTING_PITCHERS,
} from "../../../common/constants.baseball.ts";
import { choice } from "../../../common/random.ts";
import type { Position } from "../../../common/types.baseball.ts";
import { maxBy, orderBy } from "../../../common/utils.ts";
import { fatigueFactor } from "./fatigueFactor.ts";
import { CLOSER_INDEX, getStartingPitcher } from "./getStartingPitcher.ts";
import type { PlayerGameSim, TeamGameSim } from "./types.ts";
import { g } from "../../util/index.ts";
import { isPitcherCapable } from "../team/rosterComposition.baseball.ts";
import type { RpRole } from "../../../common/rpRoles.ts";

type GamePositions<DH extends boolean> =
	| Exclude<Position, "SP" | "RP" | "DH">
	| "P"
	| (DH extends true ? "DH" : never);

type PlayerInGame<DH extends boolean> = {
	p: PlayerGameSim;
	battingOrder: number;
	pos: GamePositions<DH>;
};

const NUM_BATTERS_PER_SIDE = 9;

// Below this many healthy pitchers, position players may still have to pitch
const MIN_HEALTHY_ARMS = 10;

// Playing time, in plate appearances against a full-time regular (about this many a team game)
const PA_PER_GAME = 4.2;
const PT_LESS_SHARE = 0.75;
const PT_MORE_SHARE = [0.25, 0.5] as const;
// A bench player on "+"/"++" only takes over a position he plays at most this much worse than the regular
const PT_FIELD_DROP = 15;

export type ReliefRole = "closer" | "setup" | "fireman" | "strikeout" | "long" | "low" | "normal";

// Relievers after the closer in the depth chart who get the 7th and 8th of a close game
const NUM_SETUP_MEN = 2;
// A reliever who pitched this many days in a row rests the next one
const MAX_DAYS_IN_ROW = 2;
// The pitcher picked to start the next game does only if at least this fresh (Fresh column on the Pitching page)
export const NEXT_START_MIN_FRESH = 0.85;

type Depth = Record<"pitchers" | "batters", PlayerGameSim[]>;

class Team<DH extends boolean> {
	t: TeamGameSim;
	dh: DH;
	allStarGame: boolean;
	playoffs: boolean;
	playersByPid: Record<number, PlayerGameSim>;
	playersInGame: Record<number, PlayerInGame<DH>>;
	playersInGameByPos: Record<GamePositions<DH>, PlayerInGame<DH>>;
	playersInGameByBattingOrder: [
		PlayerInGame<DH>,
		PlayerInGame<DH>,
		PlayerInGame<DH>,
		PlayerInGame<DH>,
		PlayerInGame<DH>,
		PlayerInGame<DH>,
		PlayerInGame<DH>,
		PlayerInGame<DH>,
		PlayerInGame<DH>,
	];

	atBat: number;
	subIndex: number;
	pinchHitCover?: { hitter: PlayerGameSim; defender: PlayerGameSim };
	numPinchHitters = 0;
	numDefensiveSubs = 0;
	// Playing time plan for this game (see planPlayingTime)
	resting = new Set<number>();
	ptAssigned: Partial<Record<string, PlayerGameSim>> = {};
	saveSituation: "saveOnly" | "saveOrHold" | undefined;

	// Depth chart, but adjusted to remove injured players and capped at the number of active players
	depth: Depth;

	constructor(t: TeamGameSim, dh: DH, allStarGame: boolean, playoffs: boolean) {
		this.t = t;
		this.dh = dh;
		this.allStarGame = allStarGame;
		this.playoffs = playoffs;

		this.playersInGame = {};

		this.depth = this.initDepth();

		this.atBat = -1;
		this.subIndex = -1;

		this.playersByPid = {};
		for (const p of this.t.player) {
			this.playersByPid[p.id] = p;
		}

		this.playersInGameByPos = {} as any;
		this.playersInGameByBattingOrder = [] as any;
		this.rebuildIndexes();
	}

	initDepth() {
		// For pitchers, slots have meaning (starter, closer, etc) so maybe it's best to just leave the injured players there and just add extra players
		let numPitchersCurrent = 0;
		const pitchers = [];

		// CPU clubs with a real staff don't dress position players as relievers just
		// because the roster has fewer than NUM_ACTIVE_PITCHERS arms. The user's depth
		// chart is used exactly as set.
		const aiTeam =
			!g.get("userTids").includes(this.t.id) || g.get("spectator");
		const healthyArms = this.t.depth.P.filter(
			(p) => !p.injured && isPitcherCapable(p),
		).length;
		const onlyArms = aiTeam && healthyArms >= MIN_HEALTHY_ARMS;

		for (const p of this.t.depth.P) {
			if (onlyArms && !isPitcherCapable(p)) {
				continue;
			}
			pitchers.push(p);
			if (!p.injured) {
				numPitchersCurrent += 1;

				if (NUM_ACTIVE_PITCHERS === numPitchersCurrent) {
					break;
				}
			}
		}

		// Starting pitcher: the one the user picked for this game if he's healthy and fresh enough, else the rotation
		const chosen =
			this.t.nextStarterPid === undefined || this.allStarGame
				? undefined
				: pitchers.find((p) => p.id === this.t.nextStarterPid);
		const startingPitcher =
			chosen &&
			!chosen.injured &&
			fatigueFactor(chosen.pFatigue ?? 0, chosen.compositeRating.workhorsePitcher) >=
				NEXT_START_MIN_FRESH
				? chosen
				: getStartingPitcher(pitchers, this.allStarGame);

		this.planPlayingTime();

		// This handles replacements for injured players
		this.playersInGame = this.getStartingPlayersInGame(startingPitcher);

		// For starting lineup, sub in top bench players
		const inputBatters = this.dh ? this.t.depth.D : this.t.depth.DP;
		// Start with starting lineup (already guaranteed healthy from getStartingPlayersInGame)
		const batters = inputBatters.filter((p) => this.playersInGame[p.id]);
		// Add healthy bench players
		let numBattersCurrent = batters.length;
		for (const p of this.benchOrder(inputBatters)) {
			if (this.playersInGame[p.id]) {
				// Already a starter
				continue;
			}

			if (!p.injured) {
				batters.push(p);
				numBattersCurrent += 1;

				if (NUM_ACTIVE_BATTERS === numBattersCurrent) {
					break;
				}
			}
		}

		return {
			pitchers,
			batters,
		};
	}

	usesPlayingTime() {
		const defense = this.dh ? this.t.depth.D : this.t.depth.DP;
		return defense.some((p) => (p.ptModifier ?? 1) !== 1);
	}

	// A position player's plate appearances this season against a full-time regular's
	ptShare(p: PlayerGameSim) {
		const t = this.t as any;
		const games = (t.won ?? 0) + (t.lost ?? 0) + (t.tied ?? 0);
		return games > 0 ? ((p as any).seasonStats?.pa ?? 0) / (games * PA_PER_GAME) : 0;
	}

	// Playing time (ptModifier, set by the user on the Defense page; always 1 for AI clubs), measured in plate
	// appearances. A starter on "-" sits once he is past 75% of a regular's, on "0" always; a bench player on "+"/"++"
	// starts until he reaches 25%/50%, in place of the regular at a position he can play (at most PT_FIELD_DROP worse
	// there) who is furthest ahead of his own share. Regulars on "+"/"++" are never moved.
	planPlayingTime() {
		if (this.allStarGame || !this.usesPlayingTime()) {
			return;
		}
		const lineup = this.dh ? this.t.depth.L : this.t.depth.LP;
		const defense = this.dh ? this.t.depth.D : this.t.depth.DP;
		const starters = lineup.filter((p) => p.id !== -1);
		const startTarget = (p: PlayerGameSim) => {
			const m = p.ptModifier ?? 1;
			return m <= 0 ? 0 : m < 1 ? PT_LESS_SHARE : 1;
		};
		for (const p of starters) {
			const target = startTarget(p);
			if (!p.injured && (target === 0 || (target < 1 && this.ptShare(p) > target))) {
				this.resting.add(p.id);
			}
		}
		const starterIds = new Set(starters.map((p) => p.id));
		for (const b of defense) {
			const m = b.ptModifier ?? 1;
			const target = m >= 1.5 ? PT_MORE_SHARE[1] : m >= 1.25 ? PT_MORE_SHARE[0] : 0;
			if (starterIds.has(b.id) || b.injured || target === 0 || this.ptShare(b) >= target) {
				continue;
			}
			let best: { pos: string; s: PlayerGameSim; ahead: number } | undefined;
			for (const s of starters) {
				const pos = s.lineupPos as string | undefined;
				if (
					!pos ||
					pos === "P" ||
					s.injured ||
					this.resting.has(s.id) ||
					(s.ptModifier ?? 1) > 1 ||
					this.ptAssigned[pos]
				) {
					continue;
				}
				if (pos !== "DH" && ((b.ovrs as any)[pos] ?? 0) < ((s.ovrs as any)[pos] ?? 0) - PT_FIELD_DROP) {
					continue;
				}
				const ahead = this.ptShare(s) - startTarget(s);
				if (!best || ahead > best.ahead) {
					best = { pos, s, ahead };
				}
			}
			if (best) {
				this.resting.add(best.s.id);
				this.ptAssigned[best.pos] = b;
			}
		}
	}

	// Who dresses on the bench (NUM_ACTIVE_BATTERS minus the lineup). AI clubs keep the depth chart order. With playing
	// time in use: a backup catcher first, then by playing time, and among equals whoever has played less, so a bench
	// bigger than the dugout takes turns. "0" players come last.
	benchOrder(inputBatters: PlayerGameSim[]) {
		if (!this.usesPlayingTime()) {
			return inputBatters;
		}
		const bench = inputBatters.filter((p) => !this.playersInGame[p.id]);
		const sorted = orderBy(
			bench,
			[(p) => ((p.ptModifier ?? 1) <= 0 ? 1 : 0), (p) => p.ptModifier ?? 1, (p) => this.ptShare(p)],
			["asc", "desc", "asc"],
		);
		const catcherInGame = Object.values(this.playersInGame).some((x) => x.pos === "C");
		const backupCatcher = catcherInGame
			? maxBy(
					sorted.filter((p) => !p.injured && (p.ptModifier ?? 1) > 0),
					(p) => (p.ovrs as any).C ?? 0,
				)
			: undefined;
		return backupCatcher ? [backupCatcher, ...sorted.filter((p) => p !== backupCatcher)] : sorted;
	}

	// This uses this.t.depth rather than this.depth because we want to search the entire roster for injury replacements
	getStartingPlayersInGame(startingPitcher: PlayerGameSim) {
		const playersInGame: Team<DH>["playersInGame"] = {};

		const lineup = this.dh ? this.t.depth.L : this.t.depth.LP;
		const defense = this.dh ? this.t.depth.D : this.t.depth.DP;

		// -1 if not found
		const pitcherBattingOrder = lineup.findIndex((p) => p.id === -1);

		playersInGame[startingPitcher.id] = {
			p: startingPitcher,
			battingOrder: pitcherBattingOrder,
			pos: "P",
		};

		// Track the first position in the batting order with a substitution, so we can leave anyone above that unsorted
		let minBattingOrderWithSubstitution: number | undefined;

		const numPositionPlayers = this.dh
			? NUM_BATTERS_PER_SIDE
			: NUM_BATTERS_PER_SIDE - 1;
		const bench = defense.slice(numPositionPlayers);
		for (let j = 0; j < numPositionPlayers; j++) {
			const i =
				pitcherBattingOrder >= 0 && j >= pitcherBattingOrder ? j + 1 : j;
			let p = lineup[i];
			if (!p) {
				throw new Error("Not enough players");
			}

			if (p.id === -1) {
				// Pitcher was already handled above
				continue;
			}

			const pos = p.lineupPos as GamePositions<DH>;

			if (pos === "P") {
				throw new Error("Should never happen");
			}

			if (p.injured || playersInGame[p.id] || this.resting.has(p.id)) {
				// Player is injured, resting (playing time) or already in game at another position (maybe pitcher), pick
				// someone off the bench: the one playing time sent in, else the best at the position
				const sortedBench = orderBy(
					bench,
					[(p) => (p.injured ? 1 : 0), (p) => ((p.ptModifier ?? 1) <= 0 ? 1 : 0), (p) => p.ovrs[pos]],
					["asc", "asc", "desc"],
				);
				const assigned = this.ptAssigned[pos];
				const p2 =
					assigned && !assigned.injured && !playersInGame[assigned.id]
						? assigned
						: sortedBench.find((p) => !playersInGame[p.id] && !this.resting.has(p.id));
				let found = false;
				if (!p2) {
					// If we're looking for a sub because the player is injured (not because they are already in the game somehow), then just keep the injured player in the game
					if (playersInGame[p.id]) {
						throw new Error("Not enough players");
					}
				} else {
					p = p2;
					found = true;
				}

				if (found && minBattingOrderWithSubstitution === undefined) {
					minBattingOrderWithSubstitution = i;
				}
			}

			if (p.id === -1) {
				playersInGame[startingPitcher.id] = {
					p: startingPitcher,
					battingOrder: i,
					pos: "P",
				};
			} else {
				playersInGame[p.id] = {
					p,
					battingOrder: i,
					pos,
				};
			}
		}

		// If there were subs in the starting batting order, auto sort
		if (minBattingOrderWithSubstitution !== undefined) {
			const originalBattingOrder = [];
			for (const p of Object.values(playersInGame)) {
				if (p.battingOrder >= 0) {
					originalBattingOrder[p.battingOrder] = p;
				}
			}

			// Sort only for the substituted player and after - this formula is not the same as the new sortBattingOrder, would need raw ratings for that
			const newBattingOrder = [
				...originalBattingOrder.slice(0, minBattingOrderWithSubstitution),
				...orderBy(
					originalBattingOrder.slice(minBattingOrderWithSubstitution),
					(p) => p.p.ovrs.DH + 0.2 * p.p.compositeRating.speed,
					"desc",
				),
			];

			for (const [i, p] of newBattingOrder.entries()) {
				p.battingOrder = i;
			}
		}

		return playersInGame;
	}

	rebuildIndexes() {
		for (const playerInGame of Object.values(this.playersInGame)) {
			this.playersInGameByPos[playerInGame.pos] = playerInGame;
			if (playerInGame.battingOrder >= 0) {
				this.playersInGameByBattingOrder[playerInGame.battingOrder] =
					playerInGame;

				playerInGame.p.battingOrder = playerInGame.battingOrder;
			}

			if (playerInGame.p.subIndex === undefined) {
				this.subIndex += 1;
				playerInGame.p.subIndex = this.subIndex;
			}

			// If players can switch positions mid game, this doens't make sense. Would need to store an array to track all positions
			playerInGame.p.pos = playerInGame.pos;
		}
	}

	getBatter() {
		return this.playersInGameByBattingOrder[this.atBat]!;
	}

	getOnDeck() {
		return this.playersInGameByBattingOrder[
			(this.atBat + 1) % NUM_BATTERS_PER_SIDE
		]!;
	}

	advanceToNextBatter() {
		this.atBat = (this.atBat + 1) % NUM_BATTERS_PER_SIDE;
	}

	moveToPreviousBatter() {
		this.atBat -= 1;
		if (this.atBat < 0) {
			this.atBat = NUM_BATTERS_PER_SIDE - 1;
		}
	}

	// The closer is the pitcher after the rotation in the depth chart. The other roles are tags the user picks on the
	// Pitching page (p.rpRoles, several allowed); with no "setup" tag on the team the two arms after the closer set up,
	// which is also how AI clubs work. A reliever who pitched on each of the last two days sits out, unless nobody else
	// is left. Each situation tries its own tag first, then falls back step by step to the ordinary bullpen.
	getBestReliefPitcher(role: ReliefRole):
		| {
				p: PlayerGameSim;
				value: number;
		  }
		| undefined {
		// 5 starters normally, but only 4 in the playoffs so use the last starter as a reliever, unless one of the top 4 starters is injured
		let numStartingPitchers = NUM_STARTING_PITCHERS;
		if (this.playoffs) {
			const starters = this.depth.pitchers.slice(0, NUM_STARTING_PITCHERS - 1);
			if (starters.every((p) => !p.injured)) {
				numStartingPitchers = NUM_STARTING_PITCHERS - 1;
			}
		}

		const availablePitchers = this.depth.pitchers
			.map((p, i) => ({
				starter: i < numStartingPitchers,
				p,
				index: i,
				value:
					fatigueFactor(
						p.pFatigue + p.stat.pc,
						p.compositeRating.workhorsePitcher,
					) * p.compositeRating.pitcher,
			}))
			.filter((p) => p.p.subIndex === undefined);

		const choiceWeight = (p: (typeof availablePitchers)[number]) =>
			0.01 + p.value ** 2;

		const healthyAll = availablePitchers.filter((p) => !p.p.injured);
		const rested = healthyAll.filter(
			(p) => p.starter || (p.p.pDaysInRow ?? 0) < MAX_DAYS_IN_ROW,
		);
		const healthyPitchers = rested.some((p) => !p.starter)
			? rested
			: healthyAll;

		if (this.allStarGame) {
			return healthyPitchers[0];
		}

		const relievers = healthyPitchers.filter((p) => !p.starter);
		const tagged = (tag: RpRole) =>
			relievers.filter((p) => p.p.rpRoles?.includes(tag));
		const best = (list: typeof relievers) =>
			list.length > 0 ? [...list].sort((a, b) => b.value - a.value)[0] : undefined;

		const closerSlot = relievers.find((p) => p.index === CLOSER_INDEX);
		const closer =
			closerSlot ??
			best(tagged("closerBackup")) ??
			relievers[0] ??
			choice(healthyPitchers, choiceWeight) ??
			choice(availablePitchers);

		if (role === "closer") {
			return closer;
		}

		const teamHasSetupTags = this.depth.pitchers.some((p) => p.rpRoles?.includes("setup"));
		const setupMen = teamHasSetupTags
			? tagged("setup").filter((p) => p !== closer)
			: relievers.filter(
					(p) =>
						p !== closer &&
						p.index > CLOSER_INDEX &&
						p.index <= CLOSER_INDEX + NUM_SETUP_MEN,
				);
		// Arms kept for the big moments: in ordinary spots they mostly sit
		const highLeverage = new Set([
			closer,
			...setupMen,
			...tagged("fireman"),
			...tagged("strikeout"),
			...tagged("closerBackup"),
		]);
		const others = relievers.filter((p) => !highLeverage.has(p));

		if (role === "strikeout") {
			const pick = best(tagged("strikeout").filter((p) => p !== closer)) ?? best(tagged("fireman").filter((p) => p !== closer));
			if (pick) return pick;
		}
		if (role === "fireman" || role === "strikeout") {
			// The most dangerous moment: a fireman, else the best arm short of the closer
			const pick = best(tagged("fireman").filter((p) => p !== closer)) ?? best(relievers.filter((p) => p !== closer));
			if (pick) return pick;
		}
		if (role === "setup" && setupMen.length > 0) {
			return best(setupMen);
		}
		if (role === "long") {
			// Starter gone early: a long man, else the reliever with the most endurance among the ordinary arms
			const longMen = tagged("long");
			if (longMen.length > 0) return best(longMen);
			const pool = others.length > 0 ? others : relievers.filter((p) => p !== closer);
			if (pool.length > 0) {
				return [...pool].sort(
					(a, b) =>
						b.p.compositeRating.workhorsePitcher - a.p.compositeRating.workhorsePitcher ||
						b.value - a.value,
				)[0];
			}
		}
		if (role === "low") {
			const mopUp = tagged("mopup");
			if (mopUp.length > 0) return choice(mopUp, choiceWeight);
			if (others.length > 0) return choice(others, (p) => 0.01 + (1.2 - p.value) ** 2);
		}

		const skipCloser = Math.random() < 0.9;
		const skipHighLeverage = Math.random() < 0.75;

		const reliever =
			choice(
				relievers.filter(
					(p) =>
						(!skipCloser || p !== closer) &&
						(!skipHighLeverage || p === closer || !highLeverage.has(p)),
				),
				choiceWeight,
			) ??
			// Nobody else rested: the closer (or a setup man) rather than a tired arm
			choice(relievers, choiceWeight);

		if (reliever) {
			return reliever;
		}

		const pitcher = choice(availablePitchers, choiceWeight);

		if (pitcher) {
			return pitcher;
		}

		// No pitchers available, go to position players
		const availablePitchers2 = this.depth.batters
			.map((p, i) => ({
				starter: false,
				p,
				index: i,
				value:
					fatigueFactor(
						p.pFatigue + p.stat.pc,
						p.compositeRating.workhorsePitcher,
					) * p.compositeRating.pitcher,
			}))
			.filter((p) => p.p.subIndex === undefined);

		return choice(availablePitchers2, choiceWeight);
	}

	getInjuryReplacement(
		pos: Exclude<Position, "SP" | "RP">,
	): PlayerGameSim | undefined {
		const availablePlayers = this.depth.batters.filter(
			(p) => p.subIndex === undefined && !p.injured &&
				(p !== this.pinchHitCover?.defender || this.playersInGame[this.pinchHitCover.hitter.id]?.pos === pos),
		);

		let replacement;
		let maxOvr = -Infinity;
		for (const p of availablePlayers) {
			const ovr = p.ovrs[pos];
			if (ovr > maxOvr) {
				maxOvr = ovr;
				replacement = p;
			}
		}

		return replacement;
	}

	substitution(off: PlayerInGame<DH>, on: PlayerGameSim) {
		this.playersInGame[on.id] = {
			p: on,
			battingOrder: off.battingOrder,
			pos: off.pos,
		};

		delete this.playersInGame[off.p.id];

		this.rebuildIndexes();
	}
}

export default Team;
